import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Boxes,
  GitMerge,
  ShieldCheck,
  Loader2,
  Mail,
  KeyRound,
  ArrowRight,
} from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { isApiError } from '@/lib/errors';

interface LocationState {
  from?: { pathname: string };
}

const HIGHLIGHTS = [
  { icon: Boxes, title: 'Connect anything', text: 'Databases, files, warehouses & APIs in minutes.' },
  { icon: BarChart3, title: 'Quality on autopilot', text: 'AI tracks drift against learned baselines.' },
  { icon: GitMerge, title: 'One golden record', text: 'Fuzzy-match duplicates across sources.' },
  { icon: ShieldCheck, title: 'PII-aware by default', text: 'Auto-classification + dynamic masking.' },
];

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen overflow-hidden bg-ink-50 dark:bg-ink-950">
      {/* Brand panel */}
      <div className="relative hidden flex-1 flex-col justify-between overflow-hidden bg-sidebar-gradient p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-24 h-[26rem] w-[26rem] rounded-full bg-accent-violet/15 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_60%,rgba(16,185,129,0.10),transparent_55%)]" />

        <div className="relative flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-400 to-brand-700 text-base font-bold text-white shadow-glow">
            n
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg font-semibold tracking-tight text-white">
              nlytics
            </div>
            <div className="font-mono text-[10px] tracking-[0.22em] text-slate-400">
              ENTERPRISE DATA MANAGEMENT
            </div>
          </div>
        </div>

        <div className="relative max-w-lg">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-400/[0.08] px-3 py-1">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-400" />
            </span>
            <span className="font-mono text-[10px] font-semibold tracking-[0.22em] text-brand-200">
              ENTERPRISE READY · v1.0
            </span>
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight">
            The command centre for your company&rsquo;s data.
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300">
            Bring it together, understand it, keep it clean — all in one governed surface powered
            by AI.
          </p>

          <ul className="mt-10 grid grid-cols-2 gap-4">
            {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
              <li
                key={title}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm transition hover:border-white/15"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-500/20 text-brand-300">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="mt-3 text-sm font-semibold text-white">{title}</div>
                <div className="mt-1 text-xs text-slate-400">{text}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center justify-between text-xs text-slate-500">
          <span className="font-mono">© nlytics — Enterprise Data Management</span>
          <div className="flex items-center gap-4 font-mono text-[10px] tracking-wider">
            <span>SOC 2</span>
            <span>·</span>
            <span>GDPR</span>
            <span>·</span>
            <span>PDPL</span>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center px-6 lg:w-[480px] lg:flex-none lg:px-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 lg:hidden">
            <div className="flex items-center gap-2 text-ink-900 dark:text-ink-50">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-brand-400 to-brand-700 text-base font-bold text-white shadow-glow">
                n
              </div>
              <span className="font-display text-lg font-semibold tracking-tight">nlytics</span>
            </div>
          </div>

          <div className="eyebrow eyebrow-dot mb-3">
            <span>SIGN IN</span>
          </div>
          <h2 className="h-display">Welcome back</h2>
          <p className="mt-2 text-sm text-ink-500 dark:text-ink-400">Sign in to your workspace.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {error && (
              <div
                className="flex items-start gap-2.5 rounded-lg border border-accent-crimson/25 bg-accent-crimson/[0.06] px-3 py-2.5 text-sm text-accent-crimson"
                role="alert"
              >
                <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-crimson" />
                <span className="break-words font-medium">{error}</span>
              </div>
            )}

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-ink-700 dark:text-ink-200">Email</span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400 dark:text-ink-500" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="h-11 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 pl-10 pr-3 text-sm text-ink-900 dark:text-ink-50 placeholder:text-ink-400 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </div>
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-ink-700 dark:text-ink-200">Password</span>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400 dark:text-ink-500" />
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 pl-10 pr-3 text-sm text-ink-900 dark:text-ink-50 placeholder:text-ink-400 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary group h-11 w-full text-sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 rounded-lg border border-brand-100 dark:border-brand-800/40 bg-brand-50/60 dark:bg-brand-900/25 px-3 py-2.5 text-center text-xs text-ink-600 dark:text-ink-300">
            <span className="font-mono text-[10px] tracking-wider text-brand-700">DEMO ACCOUNT ·</span>{' '}
            <span className="font-mono font-semibold text-ink-800 dark:text-ink-100">admin@nlytics.app</span> /{' '}
            <span className="font-mono font-semibold text-ink-800 dark:text-ink-100">password</span>
          </p>
        </div>
      </div>
    </div>
  );
}
